/**
 * theme.ts — user-selectable light/dark theme.
 *
 * The app DEFAULTS TO LIGHT (matching the marketing site) until the user
 * explicitly picks dark or system; the CSS's `:root` tokens are dark with
 * `:root.light` overrides (tokens.css), so light is applied by putting
 * `.light` on <html>. The preference persists in localStorage under
 * `cw-theme` as 'light' | 'dark' | 'system' and applies EVERYWHERE,
 * auth pages included — the funnel stays one colour in both directions
 * (fresh visitor: light www → light sign-in → light app; a dark-pref
 * user gets dark sign-in → dark app).
 *
 * index.html carries a tiny pre-paint copy of resolve/apply so the first
 * frame renders in the right theme; this module is the runtime source of
 * truth after hydration (menu selector, system-preference tracking).
 */
import { DEMO } from '@/lib/demo';
import { useSyncExternalStore } from 'react';

export type ThemePref = 'light' | 'dark' | 'system';

// Demo builds ALWAYS open light and never persist a choice — the
// marketing site embeds these frames on its light pages, so a stored
// dark preference (or a dark OS) must not break landing-page parity.
// The picker still works in-session so the feature demos honestly.
let demoPref: ThemePref = 'light';

const STORAGE_KEY = 'cw-theme';
const LIGHT_MQ = '(prefers-color-scheme: light)';
const THEME_COLOR: Record<'light' | 'dark', string> = {
  dark: '#0d0d0f',
  light: '#f9f7f5',
};

const listeners = new Set<() => void>();

export function getThemePref(): ThemePref {
  if (DEMO) return demoPref;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    /* storage unavailable (private mode etc.) — fall through */
  }
  return 'light';
}

function resolve(pref: ThemePref): 'light' | 'dark' {
  if (pref === 'system') {
    return window.matchMedia(LIGHT_MQ).matches ? 'light' : 'dark';
  }
  return pref;
}

function apply(pref: ThemePref): void {
  const mode = resolve(pref);
  document.documentElement.classList.toggle('light', mode === 'light');
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[mode]);
}

export function setThemePref(pref: ThemePref): void {
  if (DEMO) {
    demoPref = pref; // session-only; every load starts light again
  } else {
    try {
      localStorage.setItem(STORAGE_KEY, pref);
    } catch {
      /* non-fatal — theme still applies for this session */
    }
  }
  apply(pref);
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Current stored preference, reactive across the app. */
export function useThemePref(): ThemePref {
  return useSyncExternalStore(subscribe, getThemePref, () => 'light' as const);
}

// Track OS scheme changes while the preference is 'system'. Module-level:
// installed once on first import (main.tsx imports for the side effect).
if (typeof window !== 'undefined') {
  window.matchMedia(LIGHT_MQ).addEventListener('change', () => {
    if (getThemePref() === 'system') apply('system');
  });
  // Re-assert on load in case the pre-paint script was edited out of sync.
  apply(getThemePref());
}
